import React, { useEffect, useState } from "react";
import { useBranding } from "../../contexts/BrandingContext";
import useBrandMark from "../../hooks/useBrandMark";
import { cn } from "../ui";
import AuthBrandMark from "./AuthBrandMark";
import { iconFor } from "./loginPageIcons";
import fallbackImg from "../../Assets/Images/login_bg.png";
import authBg from "../../Assets/Images/auth_bg.png";

/**
 * AuthBrandSide — the dark marketing half of every auth screen.
 *
 * ── Why it is `surface-dark` and not the dark theme ──────────────────────────
 * This panel is dark in BOTH themes, because white text is laid over a
 * photograph. That is exactly what `--surface-dark` / `--on-surface-dark` are
 * for; reaching for `--bg-primary` here would make the copy vanish in light
 * mode. See _palette.scss, "Always-dark surfaces".
 *
 * The accent word in the headline and the highlight tiles are `--primary`, so an
 * institute that sets `Institute.primaryColor` re-brands this panel with no code
 * change (utils/applyBrandTokens.js).
 *
 * ── Whose copy it shows ──────────────────────────────────────────────────────
 *   1. A caller that passes copy (/register-institute) — always that.
 *   2. The visited institute's own panel, when it has switched one on in
 *      Settings → Branding → Login page (`branding.loginPage`).
 *   3. The product's learner copy below.
 * While an institute's branding is still on its way, it shows none of them —
 * just the dark surface — so the panel never flashes our words before theirs.
 *
 * ⚠️ HERO_IMAGE is the swap point for the default photograph. `fallbackImg`
 * renders if a photograph 404s — the institute's own included — so a bad
 * upload degrades to the old artwork instead of a bare panel.
 */
const HERO_IMAGE = authBg;

/**
 * The product's own panel copy, in the shape an institute saves its own — so the
 * settings editor can start from exactly what is on screen today.
 */
export const LOGIN_PAGE_DEFAULTS = {
  kicker: "",
  headline: "The Smarter Way\nto",
  headlineAccent: "Learn & Grow",
  blurb:
    "An all-in-one learning platform for institutes, educators and teams to " +
    "create, deliver and measure impactful learning.",
  highlights: [
    { icon: "monitor-play", title: "Live & Interactive", desc: "Engaging classes with real-time interaction" },
    { icon: "brain-circuit", title: "AI-Powered Practice", desc: "Smart quizzes, coding practice & doubt solving" },
    { icon: "line-chart", title: "Track Progress", desc: "Detailed analytics on learning and assessments" },
    { icon: "badge-check", title: "Certified Outcomes", desc: "Verify skills with industry-recognized certificates" },
  ],
  stats: [],
  footnote: "Trusted by institutes and learners to deliver exceptional education experiences.",
  overlay: "medium",
};

/** `headline` (one optional line break) + its accent words → the headline node. */
export const headlineNode = (headline, accent) => {
  const lines = String(headline || "").split("\n");
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
      {accent && (
        <>
          {headline ? " " : ""}
          <span className="tw-text-primary">{accent}</span>
        </>
      )}
    </>
  );
};

// Four tiles is the tuned width for this panel; three fills the row just as well,
// and anything else would not fit the copy under each glyph at 1280px.
const HIGHLIGHT_COLUMNS = {
  1: "tw-grid-cols-1",
  2: "tw-grid-cols-2",
  3: "tw-grid-cols-3",
  4: "tw-grid-cols-4",
};

// How much of a photograph shows through. `medium` is the original panel; an
// institute with a quieter photograph of its own campus may want `light`.
const SCRIMS = {
  light: "tw-bg-[linear-gradient(180deg,hsl(var(--surface-dark)/0.84)_0%,hsl(var(--surface-dark)/0.55)_45%,hsl(var(--surface-dark)/0.9)_100%)]",
  medium: "tw-bg-[linear-gradient(180deg,hsl(var(--surface-dark)/0.94)_0%,hsl(var(--surface-dark)/0.80)_45%,hsl(var(--surface-dark)/0.96)_100%)]",
  strong: "tw-bg-[linear-gradient(180deg,hsl(var(--surface-dark)/0.97)_0%,hsl(var(--surface-dark)/0.92)_45%,hsl(var(--surface-dark)/0.98)_100%)]",
};

/**
 * The panel itself — content in, markup out. AuthBrandSide decides WHAT to show;
 * the Login page settings preview renders this same component, so the preview
 * is the real thing rather than a sketch of it.
 *
 *   mark        useBrandMark() output
 *   pending     the brand is not known yet: no copy, no photograph
 *   preview     always visible and fills its box (the settings preview)
 *   background  photograph URL, or null for the bare dark surface
 *   overlay     light | medium | strong
 *   highlights  [{ icon: Component | key, title, desc }]
 */
export const AuthBrandPanel = ({
  mark,
  pending = false,
  preview = false,
  background,
  overlay = "medium",
  kicker,
  title,
  blurb,
  highlights = [],
  badge,
  stats,
  footnote,
}) => {
  // Probe the photograph before painting it: a missing file would otherwise leave
  // the panel a flat navy rectangle with no clue why.
  const [heroSrc, setHeroSrc] = useState(background);
  useEffect(() => {
    setHeroSrc(background);
    if (!background) return undefined;
    let live = true;
    const img = new Image();
    img.onerror = () => { if (live) setHeroSrc(fallbackImg); };
    img.src = background;
    return () => { live = false; };
  }, [background]);

  const tiles = highlights.filter((h) => h && h.title);

  return (
    // aria-hidden: everything here is marketing copy that repeats nothing the
    // form needs. A screen reader lands straight on the heading of the form.
    <aside
      aria-hidden="true"
      className={cn(
        "tw-relative tw-shrink-0 tw-overflow-hidden tw-bg-surface-dark",
        preview ? "tw-flex tw-h-full tw-w-full" : "tw-hidden tw-w-1/2 lg:tw-flex"
      )}
    >
      {heroSrc && (
        <div
          className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center"
          style={{ backgroundImage: `url(${heroSrc})` }}
        />
      )}
      {/* Two stacked scrims: a vertical one so the copy reads at the top and the
          tiles read at the foot, and a brand wash from the bottom-left corner. */}
      <div className={cn("tw-absolute tw-inset-0", SCRIMS[overlay] || SCRIMS.medium)} />
      <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(120%_90%_at_0%_100%,hsl(var(--primary)/0.32)_0%,transparent_60%)]" />

      <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-8 tw-p-10 tw-text-on-surface-dark xl:tw-p-14">
        {/* ── Brand mark ─────────────────────────────────────────────────── */}
        <div className="tw-flex tw-items-center tw-gap-3">
          <AuthBrandMark mark={mark} />
          <div className="tw-min-w-0">
            <div className="tw-truncate tw-text-xl tw-font-bold tw-leading-tight">{mark.name}</div>
            <div className="tw-truncate tw-text-[0.8125rem] tw-text-on-surface-dark/60">{mark.subtitle}</div>
          </div>
          {badge && <div className="tw-ml-auto tw-shrink-0">{badge}</div>}
        </div>

        {/* ── Headline ───────────────────────────────────────────────────── */}
        <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-center">
          {!pending && (
            <>
              {kicker && (
                <span className="tw-mb-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-primary">
                  {kicker}
                </span>
              )}
              {/* A div, not an h1: the form's own heading is the page's h1, and a
                  second one inside an aria-hidden aside would still be in the
                  document outline. */}
              <div className="tw-mb-5 tw-text-[clamp(2rem,3.2vw,3.25rem)] tw-font-bold tw-leading-[1.12] tw-tracking-[-0.02em]">
                {title}
              </div>
              {blurb && (
                <p className="tw-max-w-[30rem] tw-text-base tw-leading-relaxed tw-text-on-surface-dark/70">
                  {blurb}
                </p>
              )}
              <span className="tw-mt-8 tw-h-[3px] tw-w-16 tw-rounded-sm tw-bg-primary" />
            </>
          )}
        </div>

        {/* ── Highlights ─────────────────────────────────────────────────── */}
        {!pending && tiles.length > 0 && (
          <div className={cn("tw-grid tw-gap-5", HIGHLIGHT_COLUMNS[tiles.length] || HIGHLIGHT_COLUMNS[4])}>
            {tiles.map(({ icon, title: tileTitle, desc }) => {
              const Icon = typeof icon === "string" ? iconFor(icon) : icon;
              return (
                <div key={tileTitle} className="tw-flex tw-flex-col tw-gap-2.5">
                  <span className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/15 tw-text-primary tw-ring-1 tw-ring-primary/25">
                    <Icon size={20} />
                  </span>
                  <span className="tw-text-sm tw-font-semibold tw-leading-snug">{tileTitle}</span>
                  {desc && (
                    <span className="tw-text-xs tw-leading-relaxed tw-text-on-surface-dark/55">{desc}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Trust stats ────────────────────────────────────────────────── */}
        {!pending && stats?.length > 0 && (
          <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-rounded-lg tw-bg-on-surface-dark/5 tw-p-4 tw-ring-1 tw-ring-on-surface-dark/10">
            {stats.map(({ value, label }) => (
              <div key={label} className="tw-flex tw-flex-col tw-gap-0.5">
                <span className="tw-text-lg tw-font-bold tw-leading-tight">{value}</span>
                <span className="tw-text-xs tw-text-on-surface-dark/55">{label}</span>
              </div>
            ))}
          </div>
        )}

        {!pending && footnote && (
          <p className="tw-mb-0 tw-border-0 tw-border-t tw-border-solid tw-border-on-surface-dark/10 tw-pt-6 tw-text-xs tw-text-on-surface-dark/45">
            {footnote}
          </p>
        )}
      </div>
    </aside>
  );
};

/**
 * Props — every one optional, and the defaults are the learner panel:
 *
 *   brand      — { name, favicon, subtitle } overriding BrandingContext, for a
 *                screen that must wear the PLATFORM mark rather than the host's
 *   loginPage  — an institute's own panel (the branding payload's `loginPage`),
 *                for a screen that knows the institute from something other
 *                than the host — a student invite code. Wins over the host's;
 *                `null` means "this institute has none".
 *   kicker     — small-caps line above the headline
 *   title      — the headline node
 *   blurb      — the paragraph under it
 *   highlights — [{ icon, title, desc }], up to 4 of them
 *   badge      — node pinned to the right of the brand mark (a trial pill)
 *   stats      — [{ value, label }] rendered under the highlights
 *   footnote   — the quiet line at the foot
 */
const AuthBrandSide = ({ brand, loginPage, kicker, title, blurb, highlights, badge, stats, footnote }) => {
  const branding = useBranding();
  const mark = useBrandMark(brand);

  // A caller that names the institute's panel gets exactly that. Otherwise a
  // caller that passes its own brand owns its copy too, and the host's
  // institute may have written its own.
  const panel = loginPage !== undefined ? loginPage : !brand ? branding.loginPage : null;
  const own = panel?.enabled ? panel : null;
  const d = LOGIN_PAGE_DEFAULTS;

  const content = own
    ? {
        kicker: own.kicker,
        title: headlineNode(own.headline, own.headlineAccent),
        blurb: own.blurb,
        highlights: own.highlights || [],
        stats: own.stats || [],
        footnote: own.footnote,
        background: own.backgroundUrl || HERO_IMAGE,
        overlay: own.overlay,
      }
    : {
        kicker,
        title: title ?? headlineNode(d.headline, d.headlineAccent),
        blurb: blurb ?? d.blurb,
        highlights: highlights ?? d.highlights,
        stats,
        footnote: footnote ?? d.footnote,
        background: HERO_IMAGE,
        overlay: "medium",
      };

  return (
    <AuthBrandPanel
      mark={mark}
      pending={mark.pending}
      {...content}
      background={mark.pending ? null : content.background}
      badge={badge}
    />
  );
};

export default AuthBrandSide;
